import React from "react";
import { Trans } from "react-i18next";
import { developersBaseUrl } from "constants";
import i18n from "../../i18n";

const sgT = (key, options) => i18n.t(`einvoice:startGuide.${key}`, options);

const sgRich = (key) => (
  <Trans i18nKey={`einvoice:startGuide.${key}`} components={{ b: <b /> }} />
);

const getProfileStep = (t) => ({
  name: t("profile.name"),
  videoUrl: "https://www.youtube.com/embed/zEj9_4BLPbo",
  title: t("profile.title"),
  description: t("profile.description"),
  bullets: ["english", "address", "vat", "contacts", "save"].map((key) => ({
    label: t(`profile.bullets.${key}.label`),
    text: t(`profile.bullets.${key}.text`),
  })),
  note: {
    title: t("profile.note.title"),
    items: [
      t("profile.note.pdf"),
      t("profile.note.companyName"),
      t("profile.note.registration"),
    ],
  },
  actions: [
    { label: t("common.fill"), to: "/profile", variant: "primary" },
  ],
});

const getSubscriptionStep = (t) => ({
  name: t("subscription.name"),
  title: t("subscription.title"),
  description: t("subscription.description"),
  actions: [
    { label: t("subscription.action"), to: "/subscription", variant: "primary" },
  ],
});

const getVatStep = (t) => ({
  name: t("vat.name"),
  videoUrl: "https://www.youtube.com/embed/yfyuo0ExqLg",
  title: t("vat.title"),
  description: sgRich("vat.description"),
  bullets: [
    sgRich("vat.bullets.location"),
    sgRich("vat.bullets.haveVat"),
    sgRich("vat.bullets.noVat"),
  ],
  note: {
    title: t("common.whatToFill"),
    items: [
      { label: t("common.country"), text: t("vat.note.countryText") },
      { label: t("vat.note.vatNumberLabel"), text: t("vat.note.vatNumberText") },
      { label: t("vat.note.reasonLabel"), text: t("vat.note.reasonText") },
      sgRich("vat.note.validity"),
    ],
    footer: sgRich("vat.note.footer"),
  },
  docLink: {
    text: t("vat.docLink.text"),
    label: t("vat.docLink.label"),
    href: `${developersBaseUrl}/linvoice/how-to-add-a-vat-number-in-lappa-invoice/`,
  },
  actions: [
    { label: t("vat.alreadyHave"), to: "/settings" },
    {
      label: t("common.fill"),
      to: "/settings/vat-registration/order",
      variant: "primary",
    },
  ],
});

const getPeppolStep = (t) => ({
  name: t("peppol.name"),
  videoUrl: "https://www.youtube.com/embed/zSIkJF8aGTo",
  title: t("peppol.title"),
  description: sgRich("peppol.description"),
  bullets: [
    sgRich("peppol.bullets.location"),
    sgRich("peppol.bullets.havePeppol"),
    sgRich("peppol.bullets.noPeppol"),
  ],
  note: {
    title: t("peppol.note.title"),
    items: [
      { label: t("common.country"), text: t("peppol.note.countryText") },
      sgRich("peppol.note.peppolId"),
    ],
    footer: sgRich("peppol.note.footer"),
  },
  docLink: {
    text: t("peppol.docLink.text"),
    label: t("peppol.docLink.label"),
    href: `${developersBaseUrl}/linvoice/guide-for-adding-a-peppol-id/`,
  },
  actions: [
    { label: t("peppol.alreadyHave"), to: "/settings" },
    {
      label: t("common.fill"),
      to: "/settings",
      variant: "primary",
    },
  ],
});

const getBankStep = (t) => ({
  name: t("bank.name"),
  videoUrl: "https://www.youtube.com/embed/3LfWV8TA3dw",
  title: t("bank.title"),
  description: t("bank.description"),
  bullets: [
    sgRich("bank.bullets.location"),
    sgRich("bank.bullets.addAccount"),
    sgRich("bank.bullets.currency"),
  ],
  note: {
    title: t("common.whatToFill"),
    items: [
      sgRich("bank.note.bankName"),
      sgRich("bank.note.accountHolder"),
      {
        label: t("bank.note.accountDetailsLabel"),
        text: t("bank.note.accountDetailsText"),
      },
      {
        label: t("bank.note.bicLabel"),
        text: t("bank.note.bicText"),
      },
    ],
    footer: sgRich("bank.note.footer"),
  },
  sections: [
    {
      title: t("bank.selection.title"),
      bullets: [
        sgRich("bank.selection.currency"),
        sgRich("bank.selection.local"),
        sgRich("bank.selection.global"),
      ],
    },
  ],
  docLink: {
    text: t("bank.docLink.text"),
    label: t("bank.docLink.label"),
    href: `${developersBaseUrl}/linvoice/how-to-add-bank-details-in-lappa-invoice/`,
  },
  actions: [
    { label: t("common.fill"), to: "/settings", variant: "primary" },
  ],
});

export const invoiceStartGuideTexts = {
  get subtitle() { return sgT("subtitle"); },
  get allDone() { return sgT("allDone"); },
  get showPreviousTasks() { return sgT("showPreviousTasks"); },
  get hideGuide() { return sgT("hideGuide"); },
  tasksLeft: ({ tasksLeft, minutesLeft }) =>
    sgT("tasksLeft", { tasksLeft, minutesLeft }),
};

/**
 * Content of the steps, keyed by the `step` field coming from the server.
 * A step is rendered only when the server returns it, so adding a key here is
 * safe even if the backend does not expose that step yet.
 */
export const invoiceStartGuideSteps = {
  get 1() { return getProfileStep(sgT); },
  get subscription() { return getSubscriptionStep(sgT); },
  get 2() { return getVatStep(sgT); },
  get 3() { return getPeppolStep(sgT); },
  get 4() { return getBankStep(sgT); },
};
